import AttachMoneyIcon from '@mui/icons-material/AttachMoney'
import CorporateFareIcon from '@mui/icons-material/CorporateFare'
import PersonIcon from '@mui/icons-material/Person'
import { Autocomplete, Box, TextField, Typography, useTheme } from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import { useEntitiesFromSearch } from 'api/search'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { OrganizationTableItem } from 'types/organizations_table'
import { useDebounce } from 'use-debounce'
import { renderColorByString } from '../DataGrid/renderColorByString'

const renderEntityIcon = type => {
  switch (type) {
    case 'deal':
      return <AttachMoneyIcon sx={{ color: '#fbc11e' }} />
    case 'organization':
      return <CorporateFareIcon sx={{ color: 'rgb(114, 229, 218)' }} />
    case 'contact':
      return <PersonIcon sx={{ color: 'rgb(255, 123, 187)' }} />
    default:
      return null
  }
}
const activeStatusAll = ['Active', 'Pending', '30 Day Notice', 'Onboarding', 'In Progress']
const EntitySearch = ({ onClose }: { onClose?: () => void }) => {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const location = useLocation()
  const theme = useTheme()
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearchTerm] = useDebounce(searchTerm, 300) // ✅ Debounce user input
  const inputRef = useRef(null)
  const { deals, organizations, products } = useEntitiesFromSearch()
  const allEntities = useMemo(() => {
    return deals && organizations ? [...deals, ...organizations] : []
  }, [organizations, deals])
  const entitiesWithIndex = allEntities?.map(item => {
    return item?.EntityType === 'deal' && activeStatusAll?.includes(item?.dealStatus)
      ? { ...item, sortIndex: 0 }
      : item?.EntityType === 'organization' && activeStatusAll?.includes(item?.status)
      ? { ...item, sortIndex: 1 }
      : item?.EntityType === 'deal' && !activeStatusAll?.includes(item?.dealStatus)
      ? { ...item, sortIndex: 1 }
      : { ...item, sortIndex: 2 }
  })
  const sortedEntities = entitiesWithIndex?.sort((a, b) => a.sortIndex - b.sortIndex) as OrganizationTableItem[]
  const filteredEntities = useMemo(() => {
    if (!debouncedSearchTerm) return [] // Show no results when input is empty
    const filtered = sortedEntities?.filter(entity =>
      entity?.name?.toLowerCase()?.includes(debouncedSearchTerm.toLowerCase())
    )
    const activeItems = filtered?.filter(result => activeStatusAll?.includes(result?.dealStatus || result?.status))
    return filtered
  }, [debouncedSearchTerm, allEntities])
  useEffect(() => {
    if (!Array.isArray(allEntities)) return
    const entitiesWithoutName = allEntities.filter(entity => !entity?.name)
    if (entitiesWithoutName.length > 0) {
      console.warn('🚨 Entities missing name field:', entitiesWithoutName)
      console.warn(
        '🚨 Entity IDs missing name:',
        entitiesWithoutName.map(entity => entity?.entityId || 'No ID')
      )
    }
  }, [allEntities])

  useEffect(() => {
    if (location.state?.focusSearch && inputRef.current) {
      inputRef.current.focus()
    }
  }, [location])

  const handleChangeItem = (_, newItem) => {
    if (!newItem || !newItem?.name) {
      setSearchTerm('')
      return
    }
    console.log(newItem)
    setSearchTerm(newItem.name)
    const pathSegments = location.pathname.split('/')
    const currentTab = pathSegments.length > 3 ? pathSegments[3] : 'details'
    const endTab = newItem?.type === pathSegments[1] ? currentTab : 'details'
    const newPath =
      newItem?.EntityType === 'organization'
        ? `/${newItem.EntityType}s/${newItem.PK}/${endTab}`
        : newItem?.EntityType === 'deal'
        ? `/${newItem.EntityType}s/${newItem.PK}/${newItem.SK}/${endTab}`
        : `/${newItem.EntityType}s/${newItem.entityId}/${endTab}`
    navigate(newPath, { state: { focusSearch: true } })
  }

  return (
    <Autocomplete
      multiple={false}
      autoFocus
      inputValue={searchTerm}
      onInputChange={(_, newValue) => setSearchTerm(newValue)}
      options={filteredEntities} // ✅ Uses filtered results
      getOptionLabel={option => option.name || ''}
      onChange={handleChangeItem}
      isOptionEqualToValue={(option, value) => option.entityId === value?.entityId}
      renderOption={(props, option) => {
        return (
          <Box
            component="li"
            {...props}
            key={`${option?.PK}::${option?.SK}`}
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}
          >
            <Box sx={{ display: 'flex', flexGrow: 1, gap: 1 }}>
              <Box>{renderEntityIcon(option.EntityType)}</Box>
              <Typography sx={{ color: theme.palette.text.primary }}>{option.name}</Typography>
            </Box>
            <Box sx={{ display: 'flex' }}>
              {option?.SK?.startsWith('DEAL_') ? (
                <Typography sx={{ color: renderColorByString(option?.dealStatus) }}>{option?.dealStatus}</Typography>
              ) : (
                <Typography sx={{ color: renderColorByString(option?.status) }}>{option?.status}</Typography>
              )}
            </Box>
          </Box>
        )
      }}
      renderInput={params => (
        <TextField
          {...params}
          autoFocus
          placeholder="Search Deals, Organizations, Contacts..."
          InputProps={{
            ...params.InputProps,
            inputRef: inputRef,
            // endAdornment: allEntities.length === 0 ? <CircularProgress color='inherit' size={18} /> : null,
          }}
        />
      )}
      clearOnEscape
      fullWidth
      freeSolo
    />
  )
}

export default EntitySearch
